import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import capitalize from 'lodash/capitalize';

import {
    TextButton,
    Button,
    FormField,
    IconPhoneCircle,
    IconMail,
} from '@zillow/constellation';
import { Col, Row } from '@zillow/zrs-comet';

import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import ClaimActions from 'app/shared/actions/ClaimActions';
import emailUtils from 'app/shared/utils/emailUtils';
import ListCell from 'app/shared/components/ListCell';
import numberUtils from 'app/shared/utils/numberUtils';
import PropertyLookupError from '../PropertyLookupError';

import { StyledFormHelp, StyledInput } from './StyledComponents';
import { CenteredSpacer, StyledHeading } from '../../StyledComponents';

const {
    GA_EVENTS: { CLAIM_LISTINGS_CLICK },
    CLAIM_LISTINGS_LABEL: {
        PHONE_LOOKUP_MODAL,
        EMAIL_LOOKUP_MODAL,
    },
} = AnalyticsConstants;

const lookupMap = {
    phone: {
        text: 'phone number',
        icon: <IconPhoneCircle fontColor="brand" size="md" />,
        type: 'tel',
        claimMethodsKey: 'phones',
        gaLabel: PHONE_LOOKUP_MODAL,
    },
    email: {
        text: 'email',
        icon: <IconMail fontColor="brand" size="md" />,
        type: 'email',
        claimMethodsKey: 'emails',
        gaLabel: EMAIL_LOOKUP_MODAL,
    },
};

const propTypes = {
    onChooseAnotherContactMethod: PropTypes.func.isRequired,
    onSearchListings: PropTypes.func.isRequired,
    setContactValue: PropTypes.func.isRequired,
    contactValue: PropTypes.string.isRequired,
    contactMethod: PropTypes.string.isRequired,
};

const defaultProps = {};

const ContactMethodLookup = ({
    onChooseAnotherContactMethod,
    onSearchListings,
    setContactValue,
    contactValue,
    contactMethod,
}) => {
    const dispatch = useDispatch();
    const [error, setError] = useState(false);
    const [propertyLookupError, setPropertyLookupError] = useState(false);
    const [isValidContactMethodType, setIsValidContactMethodType] = useState(false);

    const DUPLICATE_CONTACT_METHOD_ERROR = `You've already added this ${lookupMap[contactMethod].text}`;
    const PROPERTY_LOOKUP_API_ERROR = 'There was an error with lookup. Please try again.';
    const INVALID_CONTACT_METHOD_ERROR = `Please enter a valid ${lookupMap[contactMethod].text}`;

    const lookupListings = () => {
        dispatch(analyticsUtils.track.GAEventV2(CLAIM_LISTINGS_CLICK,
            {
                label: lookupMap[contactMethod].gaLabel,
                action: 'ClickLookupListings',
            }));
        dispatch(ClaimActions.getClaimMethods()).then(({ response }) => {
            let duplicate = false;

            for (const contactInformation of response[lookupMap[contactMethod].claimMethodsKey]) {
                if (contactInformation.value === contactValue) {
                    setError(DUPLICATE_CONTACT_METHOD_ERROR);
                    duplicate = true;
                    break;
                }
            }

            if (!duplicate) {
                onSearchListings().catch((err) => {
                    global.TEMPORARY_global_logger.error(err, 'An error occurred.');
                    setPropertyLookupError(PROPERTY_LOOKUP_API_ERROR);
                });
            }
        });
    };

    const onBlurHandler = () => (isValidContactMethodType ? setError(null) : setError(INVALID_CONTACT_METHOD_ERROR));
    const onFocusHandler = () => setError(null);
    const onKeyDownHandler = ({ key }) => ((key === 'Enter' && isValidContactMethodType && !error) && lookupListings());
    const onChangeHandler = ({ target: { value } }) => {
        if (error) {
            setError(null);
        }

        if (contactMethod === 'phone') {
            const cleanPhoneNumber = numberUtils.cleanPhoneNumber(value);
            setContactValue(cleanPhoneNumber);
            return setIsValidContactMethodType(cleanPhoneNumber.length === 10);
        }

        setContactValue(value);
        return setIsValidContactMethodType(emailUtils.isValid(value));
    };

    return (
        <>
            <CenteredSpacer marginTop="sm" marginBottom="sm">
                <StyledHeading>
                    {`Enter the contact ${lookupMap[contactMethod].text} on your listing`}
                </StyledHeading>
                <StyledHeading>You&apos;ll need access to it for verification</StyledHeading>
            </CenteredSpacer>
            <Row>
                <Col md={2} />
                <Col xs={12} md={8}>
                    <ListCell
                        addBorderBottom={true}
                        icon={lookupMap[contactMethod].icon}
                        primaryText={capitalize(contactMethod)}
                        secondaryText={`Find listings using your contact ${lookupMap[contactMethod].text}`}
                        ctaIcon={false}
                        cta={(
                            <FormField marginTop="sm">
                                <StyledInput
                                    placeholder={`Enter ${lookupMap[contactMethod].text}`}
                                    fluid={false}
                                    onChange={onChangeHandler}
                                    onBlur={onBlurHandler}
                                    onFocus={onFocusHandler}
                                    onKeyDown={onKeyDownHandler}
                                    type={lookupMap[contactMethod].type}
                                    value={contactMethod === 'phone' ?
                                        numberUtils.formatPhoneNumberInput(contactValue) : contactValue}
                                    error={!!error}
                                    duplicateError={error === DUPLICATE_CONTACT_METHOD_ERROR}
                                />
                                {error && (
                                    <StyledFormHelp
                                        error={!!error}
                                        duplicateError={error === DUPLICATE_CONTACT_METHOD_ERROR}
                                    >
                                        {error}
                                    </StyledFormHelp>
                                )}
                            </FormField>
                        )}
                    />
                </Col>
                <Col md={2} />
            </Row>
            <CenteredSpacer paddingTop="sm">
                <TextButton onClick={onChooseAnotherContactMethod}>
                    Try another contact method
                </TextButton>
            </CenteredSpacer>
            <CenteredSpacer paddingTop="lg">
                <Button
                    buttonType="primary"
                    disabled={!isValidContactMethodType || error}
                    onClick={lookupListings}
                >
                    Look up listings
                </Button>
                {propertyLookupError && <PropertyLookupError />}
            </CenteredSpacer>
        </>
    );
};

ContactMethodLookup.propTypes = propTypes;
ContactMethodLookup.defaultProps = defaultProps;

export default ContactMethodLookup;
